import { createFileRoute } from "@tanstack/react-router";
import React, { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import ApiIdentity from "@api/ApiIdentity";
import { useAuthUser } from "@stores/UseAuth";
import CookieManager from "@manager/CookieManager";
import { message } from "antd";
import { getCodeVerifier, removeCodeVerifier } from "@utils/PkceUtils";
import { getZaloAccessToken } from "@utils/ZaloApiUtils";

export const Route = createFileRoute("/callback/login/zalo-web")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const { setUser } = useAuthUser();

  useEffect(() => {
    const handleZaloCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const oauthCode = urlParams.get("code");
      const state = urlParams.get("state");
      const error = urlParams.get("error");
      const errorCode = urlParams.get("error_code");
      const errorDescription = urlParams.get("error_description");

      if (error || errorCode) {
        console.error("Zalo OAuth Error:", {
          error,
          errorCode,
          errorDescription,
        });
        navigate({ to: "/login" });
        return;
      }

      if (!oauthCode) {
        message.error("Không nhận được mã xác thực từ Zalo");
        navigate({ to: "/login" });
        return;
      }

      if (!state) {
        message.error("Không nhận được state từ Zalo");
        navigate({ to: "/login" });
        return;
      }

      const codeVerifier = getCodeVerifier(state);

      if (!codeVerifier) {
        message.error("Không tìm thấy code verifier. Vui lòng thử lại.");
        navigate({ to: "/login" });
        return;
      }

      try {
        const ZALO_APP_ID = (process.env as any).ZALO_APP_ID || "";
        const ZK = (process.env as any).ZK || "";

        if (!ZALO_APP_ID || !ZK) {
          message.error("Thiếu cấu hình Zalo");
          navigate({ to: "/login" });
          return;
        }

        const zaloTokenResponse = await getZaloAccessToken(
          oauthCode,
          codeVerifier,
          ZALO_APP_ID,
          ZK
        );

        if (zaloTokenResponse.error) {
          console.error(
            "Error getting Zalo access token:",
            zaloTokenResponse.error
          );
          navigate({ to: "/login" });
          return;
        }

        if (!zaloTokenResponse.access_token) {
          console.error("No access token received from Zalo");
          navigate({ to: "/login" });
          return;
        }

        const response = await ApiIdentity.loginZalo(
          zaloTokenResponse.access_token
        );

        if (response.error) {
          message.error(
            response.error?.error_description ||
              "Đăng nhập thất bại, vui lòng thử lại"
          );
          navigate({ to: "/login" });
          return;
        }

        const { access_token } = response?.response?.data || {};

        if (access_token) {
          await ApiIdentity.setToken(access_token);
          await CookieManager.setCookie("access_token", access_token, 30);

          try {
            const profileResponse = await ApiIdentity.getProfile();
            if (profileResponse?.response?.data) {
              setUser(profileResponse.response.data);
            }
          } catch (profileError: any) {
            console.error("Error fetching profile:", profileError);
          }

          navigate({ to: "/" });
          message.success("Đăng nhập thành công");
        } else {
          message.error("Không nhận được thông tin đăng nhập");
          navigate({ to: "/login" });
        }
      } catch (error: any) {
        console.error("Login error:", error);
        message.error("Đăng nhập thất bại, vui lòng thử lại");
        navigate({ to: "/login" });
      } finally {
        removeCodeVerifier(state);
      }
    };

    handleZaloCallback();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="text-center">
        <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 animate-spin">
          <svg
            className="w-10 h-10 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </div>
        <p className="text-gray-600">Đang xử lý đăng nhập Zalo...</p>
      </div>
    </div>
  );
}
